import React, { useState, useEffect, useCallback } from 'react';
import { Stack, Typography, Grid, Chip, Accordion, AccordionSummary, AccordionDetails, TextField, Button, IconButton, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { useSelector } from "react-redux";
import dayjs from 'dayjs';
import { CONFIG } from "../../../../config";
import axios from 'axios';

import { toast } from 'react-hot-toast';

const API_ACCOUNT_HOST = CONFIG.modules['accounts'].host;

/**
 * Formats seconds into HH:MM:SS or a simpler representation.
 * @param {number} totalSeconds
 * @returns {string}
 */
const formatDuration = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || totalSeconds === 0) {
    return "N/A";
  }
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  let formatted = '';
  if (hours > 0) formatted += `${hours}h `;
  if (minutes > 0) formatted += `${minutes}m `;
  if (seconds > 0 || formatted === '') formatted += `${seconds}s`; // Show seconds if duration is less than a minute or only seconds exist

  return formatted.trim();
};

/**
 * Formats a date string into a more readable format.
 * @param {string} dateString
 * @returns {string}
 */
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  try {
    // Example format: Apr 03, 2025 10:26 AM
    return dayjs(dateString).format('MMM DD, YYYY h:mm A'); // Use dayjs formatting
  } catch (error) {
    console.error("Error formatting date with dayjs:", error);
    return dateString; // Fallback to original string
  }
};

/**
 * Formats a date string into HH:mm format.
 * @param {string} dateString
 * @returns {string}
 */
const formatTime = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return dayjs(dateString).format('HH:mm'); // Format as HH:mm
  } catch (error) {
    console.error("Error formatting time with dayjs:", error);
    return "Invalid Date"; // Indicate error
  }
};

/**
 * Formats a date string to show only day, month and hours.
 * @param {string} dateString 
 * @returns {string}
 */
const formatDateShort = (dateString) => {
  if (!dateString) return "N/A";
  try {
    return dayjs(dateString).format('MMM DD, h:mm A');
  } catch (error) {
    console.error("Error formatting date short with dayjs:", error);
    return "Invalid Date";
  }
};

/**
 * Formats a time string to show hours, minutes and AM/PM.
 * @param {string} timeString 
 * @returns {string}
 */
const formatTimeAmPm = (timeString) => {
  if (!timeString) return "N/A";
  try {
    return dayjs(timeString).format('h:mm A');
  } catch (error) {
    console.error("Error formatting time AM/PM with dayjs:", error);
    return "Invalid Time";
  }
};

/**
 * Component to display detailed information about the current attendance record.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.currentAttendanceDetails - The attendance details to display
 * @param {boolean} props.currentAttendanceDetailsLoading - Whether the details are currently loading
 * @param {function} props.getCurrentAttendanceDetails - Function to fetch the current attendance details
 * @returns {JSX.Element | null} - The rendered component
 */
const CurrentAttendanceDetails = (props) => {
  const { currentAttendanceDetails, currentAttendanceDetailsLoading, getCurrentAttendanceDetails } = props;

  // Updating state
  const [isUpdating, setIsUpdating] = useState(false);

  // State for editing shiftEnded
  const [isEditingShiftEnded, setIsEditingShiftEnded] = useState(false);
  const [editedShiftEndedValue, setEditedShiftEndedValue] = useState(null);


  // State to store all field values (original and potentially updated values)
  const [fieldValues, setFieldValues] = useState({});

  // Initialize field values when attendance details change
  useEffect(() => {
    if (currentAttendanceDetails) {
      // Initialize with original values
      setFieldValues({
        forDate: { original: currentAttendanceDetails.forDate },
        status: { original: currentAttendanceDetails.status },
        duration: { original: currentAttendanceDetails.seconds },
        shiftStarted: { original: currentAttendanceDetails.shiftStarted },
        shiftEnded: { original: currentAttendanceDetails.shiftEnded },
        shiftStartedTime: { original: currentAttendanceDetails.shiftStartedTime },
        shiftEndedTime: { original: currentAttendanceDetails.shiftEndedTime },
        checkIn: { original: currentAttendanceDetails.checkIn },
        checkOut: { original: currentAttendanceDetails.checkOut },
        createdAt: { original: currentAttendanceDetails.createdAt },
        updatedAt: { original: currentAttendanceDetails.updatedAt },
        deleted: { original: currentAttendanceDetails.deleted }
      });
      // Initialize editedShiftEndedValue when details load or change
      setEditedShiftEndedValue(currentAttendanceDetails.shiftEnded);
      setIsEditingShiftEnded(false); // Reset editing state if details change
    }
  }, [currentAttendanceDetails]);

  // Handlers for shiftEnded editing
  const handleEditShiftEndedClick = () => {
    setEditedShiftEndedValue(fieldValues.shiftEnded.original); // Start editing with the original value
    setIsEditingShiftEnded(true);
  };

  const handleSaveShiftEndedClick = async () => {
    // TODO: Implement API call to update the shiftEnded value
    console.log('Saving shiftEnded:', editedShiftEndedValue);

    setIsUpdating(true);
    try {
      const response = await axios.put(`${API_ACCOUNT_HOST}/update-attendance`, {
        attendanceId: currentAttendanceDetails._id,
        data: {
          shiftEnded: editedShiftEndedValue
        }
      });

      if (response.data.success) {
        getCurrentAttendanceDetails();
        toast.success('Shift ended updated successfully.');
      }
    } catch (error) {
      console.error('Error updating shiftEnded:', error);
      toast.error('Failed to update shiftEnded. Please try again.');
    } finally {
      setIsUpdating(false);
    }

    // Optimistically update local state (or refetch details)
    setFieldValues(prev => ({
      ...prev,
      shiftEnded: { ...prev.shiftEnded, original: editedShiftEndedValue }
    }));
    setIsEditingShiftEnded(false);
    // Optionally call getCurrentAttendanceDetails() to refetch
  };

  const handleCancelEditShiftEnded = () => {
    setIsEditingShiftEnded(false);
    // No need to reset editedShiftEndedValue as it's reset on next edit click
  };

  const handleShiftEndedChange = (event) => {
    setEditedShiftEndedValue(event.target.value === 'true');
  };

  /**
   * Renders a field with the appropriate component based on field type.
   * 
   * @param {string} label - Label for the field
   * @param {any} value - Value to display
   * @param {string} key - Unique key for the field
   * @param {string} type - Type of field: 'text', 'select', 'date', 'time', 'duration', 'boolean'
   * @param {Array<{label: string, value: any}>} [selectOptions] - Options for select field
   * @returns {JSX.Element} - Rendered field component
   */
  const renderDetailField = (label, value, key, type = 'text', selectOptions = []) => {
    let displayValue = value;

    // Format value based on type
    switch (type) {
      case 'select':
        // For select fields, display the label of the selected option
        const selectedOption = selectOptions.find(option => option.value === value);
        displayValue = selectedOption ? selectedOption.label : 'N/A';
        break;
      case 'date':
        displayValue = formatDateShort(value);
        break;
      case 'time':
        displayValue = formatTimeAmPm(value);
        break;
      case 'duration':
        displayValue = formatDuration(value);
        break;
      case 'boolean':
        displayValue = value ? 'Yes' : 'No';
        break;
      default:
        // Default text display
        displayValue = value || 'N/A';
    }

    // For select fields, render a disabled select component
    if (type === 'select') {
      return (
        <Grid item xs={12} sm={6} key={key || label}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>{label}:</Typography>
            <FormControl variant="standard" fullWidth size="small" disabled>
              <Select
                value={value}
                displayEmpty
                renderValue={() => displayValue}
              >
                {selectOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Grid>
      );
    }

    // For all other fields, render a read-only TextField
    return (
      <Grid item xs={12} sm={6} key={key || label}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>{label}:</Typography>
          <TextField
            variant="standard"
            size="small"
            fullWidth
            value={displayValue}
            disabled
            InputProps={{
              disableUnderline: true,
              readOnly: true,
            }}
            sx={{
              '& .MuiInputBase-input.Mui-disabled': {
                WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                cursor: 'default',
              },
            }}
          />
        </Stack>
      </Grid>
    );
  };

  if (currentAttendanceDetailsLoading) {
    return <Typography>Loading details...</Typography>;
  }

  if (!currentAttendanceDetails || Object.keys(currentAttendanceDetails).length === 0) {
    return <Typography>No details available for the selected date.</Typography>;
  }

  const {
    createdAt,
    deleted,
    forDate,
    seconds,
    shiftEnded,
    shiftStarted,
    shiftStartedTime,
    shiftEndedTime,
    shiftTimings,
    shifts,
    status,
    swapped,
    updatedAt,
    checkIn,
    checkOut,
  } = currentAttendanceDetails;

  // Status options for select type
  const statusOptions = [
    { label: 'Present', value: 'present' },
    { label: 'Absent', value: 'absent' },
    { label: 'Half Day', value: 'half-day' },
    { label: 'Leave', value: 'leave' }
  ];

  return (
    <Stack>
      <Accordion defaultExpanded>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="attendance-details-content"
          id="attendance-details-header"
        >
          <Typography variant="h6">Current Attendance Details</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            {renderDetailField("For Date", forDate, "forDate", "date")}

            {/* Status Chip special handling */}
            <Grid item xs={12} sm={6} key="status">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>Status:</Typography>
                <Chip
                  label={status || 'N/A'}
                  color={status === 'present' ? 'success' : status === 'absent' ? 'error' : 'default'}
                  size="small"
                />
              </Stack>
            </Grid>

            {renderDetailField("Duration(online)", seconds, "duration", "duration")}
            {renderDetailField("Shift Started", shiftStarted, "shiftStarted", "boolean")}

            {/* Shift Ended Field - Editable */}
            <Grid item xs={12} sm={6} key="shiftEnded">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body2" color="text.secondary" sx={{ minWidth: '120px' }}>Shift Ended:</Typography>
                {!isEditingShiftEnded ? (
                  <>
                    <TextField
                      variant="standard"
                      size="small"
                      fullWidth
                      value={fieldValues.shiftEnded?.original ? 'Yes' : 'No'}
                      disabled
                      InputProps={{
                        disableUnderline: true,
                        readOnly: true,
                      }}
                      sx={{
                        '& .MuiInputBase-input.Mui-disabled': {
                          WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                          cursor: 'default',
                        },
                      }}
                    />
                    <IconButton size="small" onClick={handleEditShiftEndedClick}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </>
                ) : (
                  <>
                    <FormControl variant="standard" size="small" fullWidth>
                      <Select
                        value={editedShiftEndedValue === null ? '' : String(editedShiftEndedValue)} // Ensure value is string for Select
                        onChange={handleShiftEndedChange}
                      >
                        <MenuItem value="true">Yes</MenuItem>
                        <MenuItem value="false">No</MenuItem>
                      </Select>
                    </FormControl>
                    <IconButton size="small" onClick={handleSaveShiftEndedClick} color="primary">
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={handleCancelEditShiftEnded} color="error">
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </>
                )}
              </Stack>
            </Grid>

            {renderDetailField("Shift Started Time", shiftStartedTime, "shiftStartedTime", "time")}
            {renderDetailField("Shift Ended Time", shiftEndedTime, "shiftEndedTime", "time")}
            {shiftStartedTime && shiftEndedTime && renderDetailField("Shift Duration", dayjs(shiftEndedTime).diff(dayjs(shiftStartedTime), 'seconds'), "shiftDuration", "duration")}
            {renderDetailField("Check In Time", checkIn, "checkIn", "time")}
            {renderDetailField("Check Out Time", checkOut, "checkOut", "time")}
            {renderDetailField("Record Created", createdAt, "createdAt", "date")}
            {renderDetailField("Last Updated", updatedAt, "updatedAt", "date")}
            {deleted && renderDetailField("Deleted", "Yes", "deleted")}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

export default CurrentAttendanceDetails;
